'use client';
import React, { useEffect, useState } from 'react';
import './Index.css';
import { Box, Paper, Stack, Typography, SortDirection } from '@mui/material';
import Constants from '../../utils/constants';
import HttpService from '../../services/HttpService';
import { useStore } from '../../utils/store';
import { useNavigate } from 'react-router-dom';
import CustomPaginationActionsTable from '../../components/TablePagination';
import AddButtonComponent from '../../components/form/AddButtonComponent';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreHorzIcon from '@mui/icons-material/MoreHoriz';
import WarningDialog from '../../components/WarningDialog';
import InfoDialog from '../../components/InfoDialog';
import AlertMessageComponent from '../../components/AlertMessageComponent';
import { useSearch } from '../../components/searchContext';
import { roundToTwo } from '../../utils/storageUtil';

interface MoreMenuProps {
  row: any;
  handleMenuClick(id: any, type: any): void;
}
const MoreMenuComponent: React.FC<MoreMenuProps> = ({
  row,
  handleMenuClick,
}) => {
  const ITEM_HEIGHT = 48;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleMoreIconClick = (event: any) => {
    setAnchorEl(event?.currentTarget);
    //setCurrentMenuIcon(id);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <div key={row.id + 'moreMenu'}>
      <IconButton id={'more-button' + row.id} onClick={handleMoreIconClick}>
        <MoreHorzIcon />
      </IconButton>
      <Menu
        id={'more-menu' + row.id}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              maxHeight: ITEM_HEIGHT * 4.5,
              width: '20ch',
            },
          },
        }}
      >
        <MenuItem sx={{ fontWeight: 'bold' }}>More Links</MenuItem>

        {row.quoteStatusId === 1 && (
          <MenuItem
            sx={{ listStyleType: 'disc' }}
            key={'delete_q' + row.id}
            onClick={() => {
              handleMenuClick(row.id, 'delete');
              handleClose();
            }}
          >
            Delete Quote
          </MenuItem>
        )}
        {(row.quoteStatusId === 5 || row.quoteStatusId === 7) && (
          <MenuItem
            sx={{ listStyleType: 'disc' }}
            key={'revq' + row.id}
            onClick={() => {
              handleMenuClick(row.id, 'Revision');
              handleClose();
            }}
          >
            Create Revision
          </MenuItem>
        )}
        {row.quoteStatusId === 1 && (
          <MenuItem
            sx={{ listStyleType: 'disc' }}
            key={'aprv_q' + row.id}
            onClick={() => {
              handleMenuClick(row.id, 'approval');
              handleClose();
            }}
          >
            Send for Approval
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};
const QuoteDashboard: React.FC = () => {
  const { getAdmin } = useStore();
  const { searchQuery } = useSearch();
  const navigate = useNavigate();

  // Columns definition for the table
  const columns: any[] = [
    {
      field: 'id',
      headerName: 'Quote',
      valueGetter: (value: any, row: any) =>
        row.rootQuoteId ? row.rootQuoteId + '.' + row.revisionNumber : value,
    },
    {
      field: 'created',
      headerName: 'Date',
      valueGetter: (value: any) =>
        new Date(value).toLocaleDateString('default', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
    },
    {
      field: 'customerName',
      headerName: 'Customer',
    },
    {
      field: 'projectName',
      headerName: 'Project Name',
    },
    {
      field: 'quoteStatusId',
      headerName: 'Status',

      valueGetter: (value: any) => getStatusText(value),
    },
    {
      field: 'quoteTotalPrice',
      headerName: 'Total',
      valueGetter: (value: any) => roundToTwo(value).toFixed(2),
    },
    {
      field: 'quoteStatusId',
      headerName: '',
      renderCell: (value: any, row: any) =>
        value === 1 || value === 5 || value === 7 ? (
          <MoreMenuComponent
            row={row}
            handleMenuClick={handleAction}
          ></MoreMenuComponent>
        ) : (
          <span>-</span>
        ),
    },
  ];

  // State variables
  const [httpError, setHttpError] = useState({
    message: '',
    open: false,
  });
  const [currentActionID, setcurrentActionID] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [warningDialogOpen, setWarningDialogOpen] =
    React.useState<boolean>(false);
  const [infoDialogOpen, setInfoDialogOpen] = React.useState<boolean>(false);
  const [warningButtonTitle, setWarningButtonTitle] =
    React.useState<string>('Save');
  const [warningMessage, setWarningMessage] = React.useState<string>(
    'Are you sure you want to save as a Draft?'
  );
  const [warningMessageTitle, setWarningMessageTitle] =
    React.useState<string>('Save Draft');
  const [infoMessageTitle, setInfoMessageTitle] =
    React.useState<string>('Success');
  const [rows, setRows] = useState<any[]>([]);
  const [orderBy, setOrderBy] = useState<any>('id');
  const [order, setOrder] = useState<SortDirection>('asc');
  const handleOrderBy = (orderByColumn: any) => {
    setOrderBy(orderByColumn);
    // console.log(order);
    // console.log(orderByColumn);
    if (order === 'desc') {
      setOrder('asc');
      return;
    } else {
      setOrder('desc');
      return;
    }
  };
  const [paginate, setPaginate] = useState({
    limit: Constants.DEFAULT_PAGE_SIZE,
    offset: 0,
  });

  const [quoteStatusList, setQuoteStatusList] = React.useState<any[]>([]);
  const handleAction = (id: any, type: any) => {
    setcurrentActionID(id);
    if (type === 'Revision') {
      setWarningButtonTitle('Revision');
      setWarningMessage(
        'Are you sure you want to create a revision for this quote?.'
      );
      setWarningMessageTitle('Revision');
    }
    if (type === 'approval') {
      setWarningButtonTitle('Send');
      setWarningMessage('Are you sure you want to send this for approval?');
      setWarningMessageTitle('Send for Internal Approval');
    }
    if (type === 'delete') {
      setWarningButtonTitle('Delete');
      setWarningMessage(
        'Are you sure you want to delete this quote? This action cannot be undone.'
      );
      setWarningMessageTitle('Delete Quote');
    }
    setWarningDialogOpen(true);
    return;
  };
  const handleWarningClose = async (type: any, reasons?: any[]) => {
    if (type === 'Cancel') {
      setWarningDialogOpen(false);
    }
    if (type === 'Delete') {
      setWarningDialogOpen(false);
      await deleteQuote();
    }
    if (type === 'Revision') {
      setWarningDialogOpen(false);
      await createRevision();
    }
    if (type === 'Send') {
      setWarningDialogOpen(false);
      await updateStatus(2, currentActionID);
    }
  };
  // Fetch quote list
  const fetchData = async () => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.quoteURL,
        {
          headers: { Authorization: 'Bearer ' + getAdmin() },
          params: {
            // assuming the HttpService uses "params" for query parameters
            search: searchQuery,
            limit: paginate.limit,
            offset: paginate.offset,
          },
        }
      );

      if (response.success) {
        setRows(response.data.quotes);
        setTotalRecords(response.data.total);
        handleOrderBy('id');
      }

      console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setHttpError({
        message: error.message,
        open: true,
      });
    }
  };

  useEffect(() => {
    if (quoteStatusList.length === 0) fetchStatusList();
  }, []);

  useEffect(() => {
    fetchData();
  }, [searchQuery, paginate]);

  const updateStatus = async (quoteId: any, quoteStatusId: any) => {
    try {
      const endpoint = `${Constants.apiURL + Constants.quoteURL}/updateQuoteStatus/${quoteId}`;
      const response = await HttpService.patch(
        endpoint,
        { quoteStatusId: quoteStatusId },
        {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        }
      );
      if (response.success) {
        setInfoDialogOpen(true);
        setInfoMessageTitle('Quote Updated');
        fetchData();
      }
    } catch (error: any) {
      console.error(`PATCH ERROR:`, error);
      setHttpError({
        message: error.message,
        open: true,
      });
    }
  };

  const createRevision = async () => {
    try {
      const endpoint = `${Constants.apiURL + Constants.quoteURL}/revision/${currentActionID}`;
      const response = await HttpService.post(
        endpoint,
        {},
        {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        }
      );
      if (response.success) {
        setInfoDialogOpen(true);
        setInfoMessageTitle('Quote revision created');
        fetchData();
      }
    } catch (error: any) {
      console.error(`POST ERROR:`, error);
      setHttpError({
        message: error.message,
        open: true,
      });
    }
  };

  const deleteQuote = async () => {
    try {
      const url = `${Constants.apiURL}${Constants.quoteURL}/${encodeURIComponent(currentActionID)}`;

      const response = await HttpService.delete(url, {
        headers: { Authorization: 'Bearer ' + getAdmin() },
      });

      if (response.success) {
        setInfoDialogOpen(true);
        setInfoMessageTitle('The quote has been deleted.');
        fetchData(); // Refresh the list after deletion
      }
      console.log('DELETE Response:', response);
    } catch (error: any) {
      console.error(`DELETE ERROR:`, error);
      setHttpError({
        message: error.message,
        open: true,
      });
    }
  };

  const fetchStatusList = async () => {
    try {
      const statusList: any = await fetchBundle('fetchQuoteStatuses');
      if (statusList) {
        setQuoteStatusList([{ id: '', name: 'Select Status' }, ...statusList]);
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setHttpError({
        message: error.message,
        open: true,
      });
    }
  };
  const fetchBundle = async (type: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        const URL = `${Constants.apiURL}${Constants.refBundleURL}/${type}`;
        const response = await HttpService.get(
          URL,
          { headers: { Authorization: 'Bearer ' + getAdmin() } },
          { search: '', limit: -1, offset: 0 }
        );
        if (response.data) {
          resolve(response.data);
        } else {
          reject('Failed to Fetch List. Please try again.');
        }
        console.log('GET Response:', response);
      } catch (error: any) {
        reject('Failed to fetch list. Please try again.');
        console.error(`GET ERROR:`, error);
        setHttpError({
          message: error.message,
          open: true,
        });
      }
    });
  };
  const getStatusText = (quoteStatusId: any) => {
    const statusData: any = quoteStatusList.find(
      (item: any) => item.id.toString() === quoteStatusId.toString()
    );
    if (statusData) {
      return statusData.name;
    } else {
      return quoteStatusId;
    }
  };
  // Clear form and reset state
  const handlePagination = (paginationData: any) => setPaginate(paginationData);

  function handleCloseAlert() {
    setHttpError((prev) => {
      return {
        ...prev,
        open: false,
      };
    });
  }

  return (
    <Stack>
      <Box className="main dashboardmain">
        <section className="section dashboard">
          <div className="containergrid">
            <div className="mt-2">
              <Paper sx={{ width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">All Quotes</Typography>
                  <AddButtonComponent
                    onClick={() => {
                      navigate(`/quote-dashboard/quote/Add`);
                    }}
                    keyName="quoteaddicon"
                    label="Add New"
                  />
                </Box>
                <CustomPaginationActionsTable
                  rows={rows}
                  columns={columns}
                  totalRecords={totalRecords}
                  handlePagination={handlePagination}
                  handleRowClick={(row) => {
                    navigate(`/quote-dashboard/quote/${row.id}`);
                  }}
                  paginationModel={{
                    page: paginate.offset,
                    pageSize: paginate.limit,
                  }}
                  handleStatus={() => {}}
                  order={order}
                  orderBy={orderBy}
                  handleOrderBy={handleOrderBy}
                />
              </Paper>
            </div>
          </div>
        </section>
        <WarningDialog
          handleClose={handleWarningClose}
          message={warningMessage}
          open={warningDialogOpen}
          buttonTitle={warningButtonTitle}
          title={warningMessageTitle}
          reasonList={[]}
        ></WarningDialog>
        <InfoDialog
          handleClose={() => {
            setInfoDialogOpen(false);
            navigate('/quote-dashboard/quote');
          }}
          open={infoDialogOpen}
          title={infoMessageTitle}
        ></InfoDialog>
        <AlertMessageComponent
          type={'error'}
          open={httpError.open}
          handleClose={handleCloseAlert}
          message={httpError.message}
        />
      </Box>
    </Stack>
  );
};

export default QuoteDashboard;
